import { Translation } from 'primeng/api';

/**
 * **Español de Ecuador para PrimeNG.** Se enchufa en `providePrimeNG({ translation })`
 * (`app.config.ts`) y es la ÚNICA palanca de idioma que queda viva:
 *
 * - El default de PrimeNG es inglés duro (`primeng-config.mjs:120-146`): sin esto la
 *   cabecera dice «August 2026», los minis «Su Mo Tu We» y los botones «Today»/«Clear».
 * - El input `[locale]` de `p-datepicker` **está deprecado y no hace nada**
 *   (`datepicker.d.ts:371-375`: *«Locale property has no effect, use new i18n API instead»*).
 *
 * ⚠️ **Los arreglos de días van en orden domingo→sábado, SIEMPRE.** El índice es el de
 * `Date.getDay()` (`primeng-datepicker.mjs:3353`), no el de la columna dibujada: quien rota
 * la grilla para que arranque el lunes es `firstDayOfWeek`, no el orden del arreglo.
 * Reordenarlos para «empezar en lunes» desplazaría los nombres un día entero.
 *
 * ⚠️ **Sólo las claves que existen en `Translation`** (`primeng/api/translation.d.ts`).
 * La interfaz es cerrada y todo es opcional: lo que no se declara conserva el default inglés.
 */
export const PRIMENG_ES_EC_TRANSLATION: Translation = {
    // === Calendario ==========================================================

    dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    /**
     * Dos letras y no una: la inicial sola repite «M» en martes/miércoles y «S» en
     * sábado, y la cabecera de un calendario no se puede desambiguar por contexto.
     */
    dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'],
    monthNames: [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
    ],
    monthNamesShort: [
        'ene', 'feb', 'mar', 'abr', 'may', 'jun',
        'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
    ],

    /** La semana ecuatoriana arranca el **lunes**. El default de PrimeNG es 0 (domingo). */
    firstDayOfWeek: 1,

    /**
     * ⚠️ **Hoy no cambia ni un solo datepicker del portal**: los 18 declaran su
     * `dateFormat` en la plantilla y el componente le gana a la traducción
     * (`getDateFormat()` = `this.dateFormat || getTranslation('dateFormat')`,
     * `primeng-datepicker.mjs:3306-3307`). Está para que el que se agregue mañana
     * **sin** declararlo no herede el `mm/dd/yy` gringo del default.
     */
    dateFormat: 'dd/mm/yy',

    // === Botones y cabeceras del panel =======================================

    today: 'Hoy',
    /** También lo usa el botón *Limpiar* del menú de filtros de `p-table`. */
    clear: 'Limpiar',
    weekHeader: 'Sem',

    // === Etiquetas accesibles de la navegación del calendario ================
    // El datepicker las emite como `aria-label`/`title` de los botones de la cabecera.
    // Sin traducirlas, un lector en español las leería con fonemas ingleses — el mismo
    // defecto 3.1.1 que arrastraba `<html lang="en">`.

    chooseYear: 'Elegir año',
    chooseMonth: 'Elegir mes',
    chooseDate: 'Elegir fecha',
    prevDecade: 'Década anterior',
    nextDecade: 'Década siguiente',
    prevYear: 'Año anterior',
    nextYear: 'Año siguiente',
    prevMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
};
